import React from "react";
import { useParams } from "react-router-dom";

import { Item } from "../ItemList";
import { VehicleAccessListParams, VehicleAccessListProps } from "./interfaces";
import { VehicleAccessListAdminPanelView } from "./views";
import { VehicleAccess } from "../../interfaces";
import { useVehicleAccessApi } from "../../hooks/use-vehicle-access";
import { useVehiclesAccessApi } from "../../hooks/use-vehicles-access";

/**
 * Renders a panel containing a seachable list of vehicles for the account provided via URL and
 * allows:
 * - Navigating to an edit screen for individual vehicles
 * - Via buttons in the panel's header:
 * - Navigating to a create vehicle access screen
 * - Deleting one or more vehicle accesses
 *
 * Intended for use by admin users
 */
export const VehicleAccessListAdminPanel: React.FC = () => {
  const { accountId } = useParams<VehicleAccessListParams>();
  const { deleteVehicleAccess, deleting } = useVehicleAccessApi(); // Hook for DELETE operation
  const { data: vehicleAccesses, loading, error } = useVehiclesAccessApi(
    accountId
  ); // Hook for GET operation
  /**
   * Used to determine whether to show a dismissable badge
   * confirming that a delete operation succeeded
   *
   */
  const [deleteSuccess, setDeleteSuccess] = React.useState(false);
  const [vehicleAccessList, setVehicleAccessList] = React.useState<
    VehicleAccess[]
  >([]);

  React.useEffect(() => {
    if (vehicleAccesses) {
      setVehicleAccessList(vehicleAccesses);
    }
  }, [vehicleAccesses]);

  const handleDelete = async (items: Item[]) => {
    try {
      for (const item of items) {
        await deleteVehicleAccess(item.id);
      }

      // Update the vehicle access list state to remove the deleted vehicle access
      setVehicleAccessList((prevList) =>
        prevList.filter(
          (vehicleAccess) => !items.some((item) => item.id === vehicleAccess.id)
        )
      );

      setDeleteSuccess(true);
    } catch (error) {
      console.error("Failed to delete vehicle access:", error);
      setDeleteSuccess(false);
      // Handle errors here, possibly set an error state
    }
  };

  return (
    <VehicleAccessListAdminPanelView
      data={vehicleAccessList}
      loading={loading || deleting} // Combine loading states
      error={error} // Pass potential fetch error to the view
      accountId={accountId}
      onDelete={handleDelete}
      deleteSuccess={deleteSuccess} // Pass delete success status to the view
    />
  );
};

export const VehicleList: React.FC<VehicleAccessListProps> = () => {
  return <VehicleAccessListAdminPanel />;
};
